import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  PaginationContainer,
  PaginationShowingText,
  PaginationTotalText,
  PaginationButton,
  CurrentPageIndicator,
} from '../HotelsTable/HotelsTable.styled';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange?: (page: number) => void;
  itemsPerPage?: number;
  totalCount?: number;
  className?: string;
  disabled?: boolean;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

/**
 * Generic Pagination Component
 * Can be used with any data table to handle pagination
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  hasNextPage,
  hasPrevPage,
  onPrevPage,
  onNextPage,
  onPageChange,
  itemsPerPage = 10,
  totalCount = 0,
  className,
  disabled = false,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
}) => {
  const [hoveredPage, setHoveredPage] = React.useState<number | null>(null);

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  // Calculate the range of items being shown
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Generate page numbers to display (max 5 pages)
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    pages.push(totalPages);

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const handlePageClick = (page: number) => {
    if (disabled || page === currentPage) return;

    if (onPageChange) {
      onPageChange(page);
    } else {
      // Fallback: use existing prev/next handlers
      const diff = page - currentPage;
      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          onNextPage();
        }
      } else {
        for (let i = 0; i < Math.abs(diff); i++) {
          onPrevPage();
        }
      }
    }
  };

  return (
    <PaginationContainer
      className={className}
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        cursor: disabled ? 'not-allowed' : 'default',
      }}
    >
      {/* Page Size Selector */}
      {onPageSizeChange && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            marginRight: 'var(--spacing-4)',
          }}
        >
          <span
            style={{
              color: 'var(--color-gray-600)',
              fontSize: 'var(--font-size-14)',
              fontWeight: 500,
            }}
          >
            Show
          </span>
          <select
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            disabled={disabled}
            style={{
              padding: 'var(--spacing-1) var(--spacing-2)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-14)',
              fontWeight: 600,
              color: 'var(--color-gray-700)',
              backgroundColor: 'var(--color-white)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              outline: 'none',
            }}
            onFocus={e => {
              e.target.style.borderColor = 'var(--color-primary-500)';
              e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
            }}
            onBlur={e => {
              e.target.style.borderColor = 'var(--color-gray-300)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
      {totalCount > 0 && (
        <PaginationShowingText>
          Showing {startItem} - {endItem} of {totalCount}
        </PaginationShowingText>
      )}
      {/* <PaginationTotalText>Total {totalPages} Pages</PaginationTotalText> */}
      <PaginationButton
        onClick={onPrevPage}
        disabled={disabled || !hasPrevPage}
        $disabled={disabled || !hasPrevPage}
      >
        <ChevronLeft
          size={20}
          color={
            disabled || !hasPrevPage
              ? 'var(--color-gray-200)'
              : 'var(--color-gray-500)'
          }
        />
      </PaginationButton>

      {/* Page Numbers */}
      {pageNumbers.map((page, index) =>
        page === '...' ? (
          <CurrentPageIndicator
            key={`ellipsis-${index}`}
            style={{
              cursor: 'default',
              backgroundColor: 'transparent',
              border: 'none',
            }}
          >
            ...
          </CurrentPageIndicator>
        ) : (
          <CurrentPageIndicator
            key={page}
            onClick={() => handlePageClick(page as number)}
            onMouseEnter={() => !disabled && setHoveredPage(page as number)}
            onMouseLeave={() => setHoveredPage(null)}
            style={{
              backgroundColor:
                page === currentPage
                  ? 'var(--color-primary-500)'
                  : hoveredPage === page && !disabled
                    ? 'var(--color-gray-100)'
                    : 'transparent',
              color: page === currentPage ? 'white' : 'var(--color-gray-700)',
              cursor: disabled
                ? 'not-allowed'
                : page === currentPage
                  ? 'default'
                  : 'pointer',
              fontWeight: page === currentPage ? 600 : 500,
              fontSize: page === currentPage ? '1rem' : '0.95rem',
              transition: 'all 0.2s ease',
            }}
          >
            {page}
          </CurrentPageIndicator>
        )
      )}

      <PaginationButton
        onClick={onNextPage}
        disabled={disabled || !hasNextPage}
        $disabled={disabled || !hasNextPage}
      >
        <ChevronRight
          size={20}
          color={
            disabled || !hasNextPage
              ? 'var(--color-gray-200)'
              : 'var(--color-gray-500)'
          }
        />
      </PaginationButton>
    </PaginationContainer>
  );
};

export default Pagination;
